/**
 * Configuración de las URLs de la API según el entorno
 * Con soporte para desarrollo local sin problemas de CORS
 */

// Detectar si estamos en desarrollo local o en el servidor
const isLocalDevelopment = (): boolean => {
  if (typeof window === 'undefined') return true; // Asumimos desarrollo local durante SSR
  
  const hostname = window.location.hostname;
  // Consideramos desarrollo local si estamos en localhost o 127.0.0.1
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// URL base del servidor de pruebas
const TEST_SERVER_URL = 'https://test.i3d.ninja';

// URL para desarrollo local: apuntar directo al servidor Node para evitar proxy
const LOCAL_API_URL = (() => {
  const port = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SERVER_PORT) ? (import.meta as any).env.VITE_SERVER_PORT : '3001';
  return `http://localhost:${port}`;
})();

// Función para obtener la URL base según el entorno
const getBaseUrl = (): string => {
  // Si estamos en SSR o no hay objeto window, usamos la URL del servidor de pruebas
  if (typeof window === 'undefined') {
    console.log('Entorno SSR detectado, usando URL del servidor de pruebas:', TEST_SERVER_URL);
    return TEST_SERVER_URL;
  }
  
  // En desarrollo local, consumir la API en live para evitar problemas de CORS/local
  if (isLocalDevelopment()) {
    console.log('Entorno de desarrollo local detectado, usando LIVE como base: https://i3d.ninja');
    return 'https://i3d.ninja';
  }
  
  // En producción, usar origen del sitio (live.i3d.ninja)
  const originUrl = window.location.origin;
  console.log('Entorno de producción detectado, usando URL de origen:', originUrl);
  return originUrl;
};

const getAssetsBaseUrl = (): string => {
  if (typeof window === 'undefined') return TEST_SERVER_URL;
  if (isLocalDevelopment()) return 'https://i3d.ninja';
  return window.location.origin;
};

// Configuración de las URLs de la API
const apiConfig = {
  // URL base para las peticiones a la API
  baseUrl: getBaseUrl(),
  assetsBaseUrl: getAssetsBaseUrl(),
  
  // Rutas específicas (sin /api al inicio cuando usamos LOCAL_API_URL que ya lo incluye)
  // Migradas a esquema v1 unificado
  endpoints: {
    // Base de blog v1
    blog: '/public/api/v1/blog',
    blogPosts: '/public/api/v1/blog/posts',
    blogPostsSearch: '/public/api/v1/blog/posts/search',
    blogAuthors: '/public/api/v1/blog/authors',
    blogTags: '/public/api/v1/blog/tags',

    // Newsletter y autenticación v1
    newsletter: '/public/api/v1/newsletter',
    auth: '/public/api/v1/auth',
    // Endpoints adicionales de autenticación e invitaciones
    verifyEmail: '/public/api/v1/auth/verify-email',
    resendVerification: '/public/api/v1/auth/resend-verification',
    invites: '/public/api/v1/invites',
    acceptInvite: '/public/api/v1/invites/accept',

    // Administración de usuarios y auditoría
    users: '/public/api/v1/users',
    adminAudit: '/public/api/v1/admin/audit',

    // Privacidad y DSAR
    privacy: '/public/api/v1/privacy',

    // Contacto y cotizaciones v1
    contactMessages: '/public/api/v1/contact/messages',
    quoteRequests: '/public/api/v1/quote/requests',

    // Calculadora (público y estable)
    calculator: '/public/api/v1/calculator',
    orders: '/public/api/v1/orders',
    // Configuración pública
    googleMapsKey: '/public/api/v1/config/google-maps-key'
  }
};

// Registramos la configuración para depuración
console.log('Configuración de API inicializada (v1):', {
  baseUrl: apiConfig.baseUrl,
  blogEndpoint: apiConfig.baseUrl + apiConfig.endpoints.blog,
  blogPostsEndpoint: apiConfig.baseUrl + apiConfig.endpoints.blogPosts,
  blogPostsSearchEndpoint: apiConfig.baseUrl + apiConfig.endpoints.blogPostsSearch,
  newsletterEndpoint: apiConfig.baseUrl + apiConfig.endpoints.newsletter,
  authEndpoint: apiConfig.baseUrl + apiConfig.endpoints.auth,
  usersEndpoint: apiConfig.baseUrl + apiConfig.endpoints.users,
  adminAuditEndpoint: apiConfig.baseUrl + apiConfig.endpoints.adminAudit,
  privacyEndpoint: apiConfig.baseUrl + apiConfig.endpoints.privacy,
  contactMessagesEndpoint: apiConfig.baseUrl + apiConfig.endpoints.contactMessages,
  quoteRequestsEndpoint: apiConfig.baseUrl + apiConfig.endpoints.quoteRequests
  , calculatorEndpoint: apiConfig.baseUrl + apiConfig.endpoints.calculator
  , verifyEmailEndpoint: apiConfig.baseUrl + apiConfig.endpoints.verifyEmail,
  resendVerificationEndpoint: apiConfig.baseUrl + apiConfig.endpoints.resendVerification,
  invitesEndpoint: apiConfig.baseUrl + apiConfig.endpoints.invites,
  acceptInviteEndpoint: apiConfig.baseUrl + apiConfig.endpoints.acceptInvite
});

// Instrucciones para configurar el proxy en vite.config.ts
/**
 * IMPORTANTE: Para desarrollo local, configura un proxy en vite.config.ts:
 * 
 * server: {
 *   proxy: {
 *     '/api': {
 *       target: 'https://test.i3d.ninja',
 *       changeOrigin: true,
 *       secure: false,
 *     }
 *   }
 * }
 */

export default apiConfig;
